/**
 * EventBus - Type-safe pub/sub system for WebSocket events
 *
 * Decouples WebSocket message handling from state contexts.
 * Each context can subscribe to specific events it cares about
 * without knowing about the WebSocket implementation.
 *
 * Key features:
 * - Type-safe event payloads
 * - Multiple subscribers per event
 * - Automatic cleanup via unsubscribe
 * - Error isolation (one subscriber's error doesn't affect others)
 */

/**
 * All event types from the backend UserChannel
 * These match server/app/channels/user_channel.rb Events class
 */
export const EventTypes = {
  // Connection events
  CONNECTED: 'connected',
  SYNC: 'sync',

  // Recording lifecycle
  NEW_RECORDING: 'new_recording',
  RECORDING_COMPLETED: 'recording_completed',
  RECORDING_FAILED: 'recording_failed',
  RECORDING_PENDING_ACK: 'recording_pending_ack',
  RECORDING_ACKED: 'recording_acked',
  RECORDING_DELETED: 'recording_deleted',

  // Note lifecycle
  NEW_NOTE: 'new_note',
  NOTE_COMPLETED: 'note_completed',
  NOTE_FAILED: 'note_failed',
  NOTE_PUBLISHED: 'note_published',
  NOTE_PUBLICATION_FAILED: 'note_publication_failed',

  // Patient lifecycle
  PATIENT_CREATED: 'patient_created',
  PATIENT_UPDATED: 'patient_updated',
  PATIENT_DELETED: 'patient_deleted',
  PATIENT_ASSIGNED_TO_RECORDING: 'patient_assigned_to_recording',
  PATIENT_UNASSIGNED_FROM_RECORDING: 'patient_unassigned_from_recording',

  // User preferences
  DEFAULT_NOTE_TYPE_CHANGED: 'default_note_type_changed',
  DEFAULT_FOLLOWUP_NOTE_TYPE_CHANGED: 'default_followup_note_type_changed',
  USER_PREFERENCES_CHANGED: 'user_preferences_changed',

  // Note type lifecycle
  NOTE_TYPE_CREATED: 'note_type_created',
  NOTE_TYPE_UPDATED: 'note_type_updated',
  NOTE_TYPE_ARCHIVED: 'note_type_archived',

  // Organization events
  ORGANIZATION_UPDATED: 'organization_updated',
  MEMBER_ADDED: 'member_added',
  MEMBER_REMOVED: 'member_removed',
  MEMBER_ROLE_CHANGED: 'member_role_changed',
  INVITATION_CREATED: 'invitation_created',
  INVITATION_ACCEPTED: 'invitation_accepted',
  INVITATION_CANCELLED: 'invitation_cancelled',
} as const;

export type EventType = typeof EventTypes[keyof typeof EventTypes];

/**
 * Event payload types - match backend serializers
 */

export interface ConnectedPayload {
  user_id: number;
  connected_at: string;
}

export interface SyncPayload {
  recordings: RecordingPayload[];
  patients: PatientPayload[];
  user: UserPayload;
  organization: OrganizationPayload;
  note_types: NoteTypePayload[];
  synced_at: string;
}

export interface RecordingPayload {
  id: number;
  organization_id: number;
  title?: string;
  status: string;
  duration_milliseconds?: number;
  transcription?: Record<string, unknown> | string | null;
  patient_id?: number;
  patient?: PatientPayload;
  notes?: NotePayload[];
  current_note?: NotePayload;
  created_at: string;
  updated_at: string;
  error?: string;
  pending_ack?: boolean;
}

export interface NotePayload {
  id: number;
  recording_id: number;
  note_type_id: number;
  status: string;
  content?: Record<string, unknown>;
  error?: string;
  created_at: string;
  updated_at: string;
}

export interface NoteFailedPayload {
  id: number;
  status: string;
  error?: string;
  recording_id: number;
}

export interface NotePublicationPayload {
  note_id: number;
  recording_id: number;
  publication_status?: string;
  external_emr_id?: string;
  error?: string;
}

export interface PatientPayload {
  id: number;
  first_name?: string;
  last_name?: string;
  dob?: string;
  gender?: string;
  external_emr_id?: string;
  created_at: string;
  updated_at: string;
}

export interface PatientRecordingPayload {
  patient_id: number | null;
  recording_id: number;
}

export interface UserPayload {
  id: number;
  first_name?: string;
  last_name?: string;
  email: string;
  preferences: UserPreferencesPayload;
}

export interface UserPreferencesPayload {
  id: number;
  default_note_type_id?: number;
  default_followup_note_type_id?: number;
  default_language?: string;
  [key: string]: unknown;
}

export interface NoteTypeSectionPayload {
  key: string;
  name: string;
  description: string;
  position: number;
}

export interface NoteTypePayload {
  id: number;
  name: string;
  description?: string;
  template: Record<string, unknown>;
  version: number;
  is_public: boolean;
  user_id: number | null;
  organization_id: number | null;
  is_editable: boolean;
  is_archived: boolean;
  sections: NoteTypeSectionPayload[];
}

export interface NoteTypeArchivedPayload {
  id: number;
}

export interface EmrConfigPayload {
  id: number;
  emr_type: string;
  enabled: boolean;
}

export interface OrganizationPayload {
  id: number;
  name: string;
  slug: string;
  members?: MemberPayload[];
  invitations?: InvitationPayload[];
  emr_config?: EmrConfigPayload | null;
}

export interface MemberPayload {
  id: number;
  organization_id: number;
  user_id: number;
  role: string;
  user: {
    id: number;
    first_name?: string;
    last_name?: string;
    email: string;
  };
}

export interface InvitationPayload {
  id: number;
  organization_id: number;
  email: string;
  status: string;
  expires_at: string;
  invited_by_id: number;
}

export interface RecordingDeletedPayload {
  id: number;
}

export interface PatientDeletedPayload {
  id: number;
}

/**
 * Map event types to their payload types
 */
export interface EventPayloadMap {
  [EventTypes.CONNECTED]: ConnectedPayload;
  [EventTypes.SYNC]: SyncPayload;
  [EventTypes.NEW_RECORDING]: RecordingPayload;
  [EventTypes.RECORDING_COMPLETED]: RecordingPayload;
  [EventTypes.RECORDING_FAILED]: RecordingPayload;
  [EventTypes.RECORDING_PENDING_ACK]: RecordingPayload;
  [EventTypes.RECORDING_ACKED]: RecordingPayload;
  [EventTypes.RECORDING_DELETED]: RecordingDeletedPayload;
  [EventTypes.NEW_NOTE]: NotePayload;
  [EventTypes.NOTE_COMPLETED]: NotePayload;
  [EventTypes.NOTE_FAILED]: NoteFailedPayload;
  [EventTypes.NOTE_PUBLISHED]: NotePublicationPayload;
  [EventTypes.NOTE_PUBLICATION_FAILED]: NotePublicationPayload;
  [EventTypes.PATIENT_CREATED]: PatientPayload;
  [EventTypes.PATIENT_UPDATED]: PatientPayload;
  [EventTypes.PATIENT_DELETED]: PatientDeletedPayload;
  [EventTypes.PATIENT_ASSIGNED_TO_RECORDING]: PatientRecordingPayload;
  [EventTypes.PATIENT_UNASSIGNED_FROM_RECORDING]: PatientRecordingPayload;
  [EventTypes.DEFAULT_NOTE_TYPE_CHANGED]: NoteTypePayload;
  [EventTypes.DEFAULT_FOLLOWUP_NOTE_TYPE_CHANGED]: NoteTypePayload;
  [EventTypes.USER_PREFERENCES_CHANGED]: UserPreferencesPayload;
  [EventTypes.NOTE_TYPE_CREATED]: NoteTypePayload;
  [EventTypes.NOTE_TYPE_UPDATED]: NoteTypePayload;
  [EventTypes.NOTE_TYPE_ARCHIVED]: NoteTypeArchivedPayload;
  [EventTypes.ORGANIZATION_UPDATED]: OrganizationPayload;
  [EventTypes.MEMBER_ADDED]: MemberPayload;
  [EventTypes.MEMBER_REMOVED]: MemberPayload;
  [EventTypes.MEMBER_ROLE_CHANGED]: MemberPayload;
  [EventTypes.INVITATION_CREATED]: InvitationPayload;
  [EventTypes.INVITATION_ACCEPTED]: InvitationPayload;
  [EventTypes.INVITATION_CANCELLED]: InvitationPayload;
}

/**
 * Subscriber callback type
 */
export type EventSubscriber<T extends EventType> = (payload: EventPayloadMap[T]) => void;

/**
 * Subscription handle for cleanup
 */
export interface Subscription {
  unsubscribe: () => void;
}

/**
 * Connection state for UI indicators
 */
export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/**
 * Connection state change callback
 */
export type ConnectionStateSubscriber = (state: ConnectionState) => void;

/**
 * EventBus class for managing event subscriptions
 */
export class EventBus {
  private subscribers: Map<EventType, Set<EventSubscriber<EventType>>> = new Map();
  private connectionStateSubscribers: Set<ConnectionStateSubscriber> = new Set();
  private currentConnectionState: ConnectionState = 'disconnected';

  /**
   * Subscribe to a specific event type
   */
  subscribe<T extends EventType>(
    eventType: T,
    callback: EventSubscriber<T>
  ): Subscription {
    if (!this.subscribers.has(eventType)) {
      this.subscribers.set(eventType, new Set());
    }

    const callbacks = this.subscribers.get(eventType)!;
    callbacks.add(callback as EventSubscriber<EventType>);

    return {
      unsubscribe: () => {
        callbacks.delete(callback as EventSubscriber<EventType>);
      },
    };
  }

  /**
   * Subscribe to multiple event types with the same callback
   */
  subscribeMany<T extends EventType>(
    eventTypes: T[],
    callback: EventSubscriber<T>
  ): Subscription {
    const subscriptions = eventTypes.map(type =>
      this.subscribe(type, callback)
    );

    return {
      unsubscribe: () => {
        subscriptions.forEach(sub => sub.unsubscribe());
      },
    };
  }

  /**
   * Subscribe to connection state changes
   */
  subscribeToConnectionState(callback: ConnectionStateSubscriber): Subscription {
    this.connectionStateSubscribers.add(callback);
    // Immediately notify of current state
    callback(this.currentConnectionState);

    return {
      unsubscribe: () => {
        this.connectionStateSubscribers.delete(callback);
      },
    };
  }

  /**
   * Publish an event to all subscribers
   */
  publish<T extends EventType>(eventType: T, payload: EventPayloadMap[T]): void {
    const callbacks = this.subscribers.get(eventType);
    if (!callbacks || callbacks.size === 0) {
      return;
    }

    callbacks.forEach(callback => {
      callback(payload);
    });
  }

  /**
   * Publish connection state change
   */
  setConnectionState(state: ConnectionState): void {
    if (this.currentConnectionState === state) return;

    this.currentConnectionState = state;
    this.connectionStateSubscribers.forEach(callback => {
      callback(state);
    });
  }

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    return this.currentConnectionState;
  }

  /**
   * Check if there are subscribers for an event
   */
  hasSubscribers(eventType: EventType): boolean {
    const callbacks = this.subscribers.get(eventType);
    return callbacks !== undefined && callbacks.size > 0;
  }

  /**
   * Get subscriber count for an event (useful for debugging)
   */
  getSubscriberCount(eventType: EventType): number {
    return this.subscribers.get(eventType)?.size ?? 0;
  }

  /**
   * Clear all subscribers (useful for testing)
   */
  clear(): void {
    this.subscribers.clear();
    this.connectionStateSubscribers.clear();
    this.currentConnectionState = 'disconnected';
  }
}

/**
 * Singleton instance for app-wide event bus
 */
let eventBusInstance: EventBus | null = null;

/**
 * Get or create the singleton EventBus instance
 */
export function getEventBus(): EventBus {
  if (!eventBusInstance) {
    eventBusInstance = new EventBus();
  }
  return eventBusInstance;
}

/**
 * Reset the singleton instance (useful for testing)
 */
export function resetEventBus(): void {
  if (eventBusInstance) {
    eventBusInstance.clear();
    eventBusInstance = null;
  }
}

/**
 * Helper to create event subscription that returns cleanup function
 * Useful for useEffect patterns
 */
export function createEventSubscriber<T extends EventType>(
  eventType: T,
  callback: EventSubscriber<T>
): () => void {
  const bus = getEventBus();
  const subscription = bus.subscribe(eventType, callback);
  return subscription.unsubscribe;
}
